import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { scheduleQueueAutomationRefetch } from "@/features/requests/utils/requestInvalidation";
import {
  allCountKeys,
  allRequestListKeys,
  customerSuccessKeys,
  designerKeys,
  invalidateQueryKeys,
  projectRequestStatisticsKeys,
  refetchQueryKeys,
  requestKeys,
  superAdminKeys,
  teamLeadKeys,
} from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

import { getStatusNameById } from "../utils/statusUtils";

const FIELDS_REQUIRING_REFETCH = [
  "statusId",
  "brandId",
  "dueDate",
  "internalDueDate",
  "assigneeId",
] as const;

interface UpdateFieldParams {
  field: string;
  requestId: number;
  value: unknown;
}

interface RequestData {
  [key: string]: unknown;
  assignee?: { designation?: string; id: number; name: string };
  complexity?: string;
  dueDate?: string;
  id: number;
  internalDueDate?: string;
  status?: { id: number; name: string };
}

interface RequestsQueryData {
  pagination: unknown;
  requests: RequestData[];
  total: number;
}

interface UpdateRequestResponse {
  request: RequestData;
  success: boolean;
}

interface MutationContext {
  previousQueries: Array<{
    data: unknown;
    queryKey: readonly unknown[];
  }>;
}

/**
 * Helper: Find all request-list queries in the cache
 * Includes standard request-list queries and role-specific queries (Designer, TeamLead)
 */
function findAllRequestListQueries(queryClient: QueryClient) {
  return allRequestListKeys.flatMap((queryKey) =>
    queryClient.getQueryCache().findAll({ queryKey }),
  );
}

/**
 * Helper: Find active queries containing a specific request
 * Only returns queries that are currently displayed on screen
 * Searches all role-specific queries (standard, designer, teamlead)
 */
function findActiveQueriesWithRequest(
  queryClient: QueryClient,
  requestId: number,
) {
  const predicate = (query: {
    observers: unknown[];
    state: { data: unknown };
  }) => {
    if (query.observers.length === 0) return false;
    const data = query.state.data as RequestsQueryData;

    return data?.requests?.some((r) => r.id === requestId) || false;
  };

  return allRequestListKeys.flatMap((queryKey) =>
    queryClient.getQueryCache().findAll({ predicate, queryKey }),
  );
}

/**
 * Helper: Invalidate all role-specific count queries
 * Ensures counts update across Designer, Team Lead, and SuperAdmin views
 */
function invalidateAllCountQueries(queryClient: QueryClient) {
  invalidateQueryKeys(queryClient, allCountKeys);
}

/**
 * Hook for updating individual request fields inline with optimistic updates
 * Provides immediate UI feedback and precise cache management
 */
export function useUpdateRequestField() {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateRequestResponse,
    Error,
    UpdateFieldParams,
    MutationContext
  >({
    mutationFn: async ({ field, requestId, value }: UpdateFieldParams) => {
      const data = { [field]: value };

      return apiClient
        .url(`/requests/list/field/${requestId}`)
        .patch(data)
        .json<UpdateRequestResponse>();
    },

    // Rollback on error
    onError: (error: unknown, _variables, context?: MutationContext) => {
      const errorMessage =
        (error as { message?: string })?.message || "Failed to update field";

      // Restore previous data on error
      if (context?.previousQueries) {
        for (const { data, queryKey } of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      toast.add({
        description: errorMessage,
        timeout: 3000,
        title: "Update failed",
        type: "error",
      });
    },

    // Optimistic update - immediately update UI before API response
    onMutate: async ({ field, requestId, value }: UpdateFieldParams) => {
      // Cancel outgoing queries to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ["request-list"] });
      await queryClient.cancelQueries({ queryKey: ["designerRequests"] });
      await queryClient.cancelQueries({ queryKey: ["teamLeadRequests"] });
      await queryClient.cancelQueries({
        queryKey: ["teamLeadUnassignedRequests"],
      });
      await queryClient.cancelQueries({ queryKey: ["teamLeadTeamRequests"] });
      await queryClient.cancelQueries({ queryKey: ["request-counts"] });
      await queryClient.cancelQueries({ queryKey: ["teamLeadCounts"] });

      // Store snapshots and apply optimistic updates
      const previousQueries: Array<{
        data: unknown;
        queryKey: readonly unknown[];
      }> = [];

      // Get all query keys that start with ["request-list"]
      const requestListQueries = findAllRequestListQueries(queryClient);

      // Apply optimistic update to each matching query
      for (const query of requestListQueries) {
        const queryKey = query.queryKey;
        const oldData = query.state.data as RequestsQueryData;

        if (oldData?.requests) {
          // Store snapshot for rollback
          previousQueries.push({ data: oldData, queryKey });

          // Apply optimistic update
          const newData = {
            ...oldData,
            requests: oldData.requests.map((request) => {
              if (request.id !== requestId) return request;

              // Apply the field update optimistically
              const updatedRequest = { ...request };

              switch (field) {
                case "assigneeId": {
                  updatedRequest.assignee =
                    value === 0 || value === null
                      ? undefined
                      : {
                          designation: updatedRequest.assignee?.designation,
                          id: value as number,
                          name: "Updating...",
                        };
                  break;
                }
                case "complexity": {
                  updatedRequest.complexity = value as string;
                  break;
                }
                case "dueDate": {
                  const dueDateUTC = value
                    ? new Date(
                        `${value as string}T00:00:00-05:00`,
                      ).toISOString()
                    : null;

                  updatedRequest.customerDueDate = dueDateUTC as string;
                  // When updating customer due date, recompute display date
                  // If no internal date exists, customer date becomes the display value
                  if (!updatedRequest.internalDueDate) {
                    updatedRequest.dueDate = dueDateUTC as string;
                  }
                  break;
                }
                case "internalDueDate": {
                  const internalDateUTC = value
                    ? new Date(
                        `${value as string}T00:00:00-05:00`,
                      ).toISOString()
                    : null;

                  updatedRequest.internalDueDate = internalDateUTC as string;
                  // When updating internal due date, it takes priority for display
                  updatedRequest.dueDate = internalDateUTC as string;
                  break;
                }
                case "statusId": {
                  updatedRequest.status = {
                    id: value as number,
                    name: getStatusNameById(value as number),
                  };
                  break;
                }
                default: {
                  updatedRequest[field] = value;
                }
              }

              return updatedRequest;
            }),
          };

          // Update the query cache
          queryClient.setQueryData(queryKey, newData);
        }
      }

      // Update counts if status or date field changed (these changes affect tab counts)
      if (
        field === "statusId" ||
        field === "dueDate" ||
        field === "internalDueDate"
      ) {
        // Invalidate all role-specific count queries (Designer, Team Lead, SuperAdmin)
        invalidateAllCountQueries(queryClient);
      }

      // Return context for rollback
      return { previousQueries };
    },

    // Immediate refetch strategy: use refetchQueries for instant UI updates
    onSettled: (_data, _error, variables) => {
      const { field, requestId } = variables;

      // Status changes are special: they can move requests between sections/tabs
      // Use invalidateQueries to mark ALL queries as stale (including collapsed sections)
      // This ensures data refreshes when sections become visible
      if (field === "statusId") {
        // Invalidate all request list queries (all sections)
        invalidateQueryKeys(queryClient, allRequestListKeys);

        // Refetch ALL section queries (not just active) to ensure immediate update
        // This is critical for status changes that move requests between sections
        refetchQueryKeys(
          queryClient,
          [teamLeadKeys.unassignedRequests, teamLeadKeys.all, designerKeys.all],
          { type: "all" },
        );

        // Refetch counts immediately (these are always visible in tabs)
        refetchQueryKeys(
          queryClient,
          [
            designerKeys.counts,
            teamLeadKeys.counts,
            customerSuccessKeys.counts,
          ],
          { type: "active" },
        );
        queryClient.invalidateQueries({ queryKey: superAdminKeys.counts });

        queryClient.invalidateQueries({
          queryKey: projectRequestStatisticsKeys.all,
        });

        scheduleQueueAutomationRefetch(queryClient);
      } else if (field === "assigneeId") {
        // Assignee changes move requests between unassigned and assigned tables
        const teamLeadQueryKeys = [
          teamLeadKeys.unassignedRequests,
          teamLeadKeys.teamRequests,
          teamLeadKeys.all,
        ];

        invalidateQueryKeys(queryClient, teamLeadQueryKeys);

        // Refetch ALL queries (not just active) to ensure immediate update
        refetchQueryKeys(
          queryClient,
          [teamLeadKeys.unassignedRequests, teamLeadKeys.teamRequests],
          { type: "all" },
        );

        // Refetch counts immediately (assignee changes affect tab counts)
        refetchQueryKeys(queryClient, [teamLeadKeys.counts], {
          type: "active",
        });

        // Invalidate project statistics (affects active slots)
        queryClient.invalidateQueries({
          queryKey: projectRequestStatisticsKeys.all,
        });
      } else if (
        FIELDS_REQUIRING_REFETCH.includes(
          field as (typeof FIELDS_REQUIRING_REFETCH)[number],
        )
      ) {
        // For date changes (not status), only refetch the specific query containing the request
        // Dates don't move requests between tabs, so we can optimize by refetching less
        const requestListQueries = findActiveQueriesWithRequest(
          queryClient,
          requestId,
        );

        for (const query of requestListQueries) {
          queryClient.refetchQueries({
            exact: true,
            queryKey: query.queryKey,
            type: "active",
          });
        }

        // ALSO refetch counts for date changes (they affect Due Today/Overdue tabs)
        if (field === "dueDate" || field === "internalDueDate") {
          refetchQueryKeys(
            queryClient,
            [designerKeys.counts, teamLeadKeys.counts],
            { type: "active" },
          );
          queryClient.invalidateQueries({ queryKey: superAdminKeys.counts });
          // Invalidate all TL and designer list queries so the due-today / overdue
          // filters reflect the new date immediately when the user switches to them
          queryClient.invalidateQueries({ queryKey: teamLeadKeys.all });
          queryClient.invalidateQueries({ queryKey: designerKeys.all });
        }
      }

      // Always invalidate the edit page request cache so changes sync to edit view
      queryClient.invalidateQueries({
        queryKey: requestKeys.requestById(requestId),
      });
    },

    onSuccess: (data: UpdateRequestResponse, variables) => {
      if (variables.field === "statusId") {
        toast.add({
          description: "Status updated successfully",
          timeout: 2000,
          title: "Success",
          type: "success",
        });

        return;
      }

      if (data?.request) {
        const requestListQueries = findAllRequestListQueries(queryClient);

        for (const query of requestListQueries) {
          const queryKey = query.queryKey;
          const oldData = query.state.data as RequestsQueryData;

          if (oldData?.requests) {
            const newData = {
              ...oldData,
              requests: oldData.requests.map((request) =>
                request.id === variables.requestId
                  ? { ...request, ...data.request } // Merge backend response
                  : request,
              ),
            };

            queryClient.setQueryData(queryKey, newData);
          }
        }
      }

      toast.add({
        description: "Field updated successfully",
        timeout: 2000,
        title: "Success",
        type: "success",
      });
    },
  });
}
